import React, { useEffect, useRef, useState } from "react";
import styles from "./HeroComponent.module.css";
import { useT } from "../../i18n";

const BRAND_WORD = "Exhale";

const HeroComponent = () => {
  const t = useT().hero;
  const taglineText = t.tagline;

  const heroRef = useRef(null);
  const cursorRef = useRef(null);
  const ctaRef = useRef(null);

  const [typed, setTyped] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [taglineTyped, setTaglineTyped] = useState("");
  const [taglineDone, setTaglineDone] = useState(false);

  // Choreography: type the brand word, blink the caret, THEN cascade the
  // rest of the hero in. The content reveal is gated on typing finishing so
  // the whole sequence flows as one motion regardless of timing.
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setTyped(BRAND_WORD);
      setRevealed(true);
      return;
    }

    let i = 0;
    let timer;
    const typeNext = () => {
      i += 1;
      setTyped(BRAND_WORD.slice(0, i));
      if (i < BRAND_WORD.length) {
        timer = setTimeout(typeNext, 115);
      } else {
        timer = setTimeout(() => setRevealed(true), 400);
      }
    };
    timer = setTimeout(typeNext, 350);
    return () => clearTimeout(timer);
  }, []);

  // Last beat: once the content cascade has settled, type the tagline in.
  // The caret only exists while typing and is gone the moment it finishes.
  useEffect(() => {
    if (!revealed) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setTaglineTyped(taglineText);
      setTaglineDone(true);
      return;
    }

    let i = 0;
    let timer;
    const typeNext = () => {
      i += 1;
      setTaglineTyped(taglineText.slice(0, i));
      if (i < taglineText.length) {
        timer = setTimeout(typeNext, 55);
      } else {
        timer = setTimeout(() => setTaglineDone(true), 550);
      }
    };
    timer = setTimeout(typeNext, 950);
    return () => clearTimeout(timer);
  }, [revealed, taglineText]);

  // Pointer-aware craft: cursor spotlight, magnetic CTA, trailing ring.
  // Desktop + motion-safe only. One rAF, only custom-property writes.
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const fine = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || reduced) return;

    let raf = 0;
    let sx = 0, sy = 0, cx = 0, cy = 0, mx = 0, my = 0;

    const onMove = (e) => {
      const r = hero.getBoundingClientRect();
      sx = e.clientX - r.left;
      sy = e.clientY - r.top;
      cx = e.clientX;
      cy = e.clientY;

      const cta = ctaRef.current;
      if (cta) {
        const b = cta.getBoundingClientRect();
        const dx = e.clientX - (b.left + b.width / 2);
        const dy = e.clientY - (b.top + b.height / 2);
        const pull = Math.max(0, 1 - Math.hypot(dx, dy) / 130);
        mx = dx * pull * 0.32;
        my = dy * pull * 0.32;
      }
      if (!raf) raf = requestAnimationFrame(apply);
    };

    const apply = () => {
      raf = 0;
      hero.style.setProperty("--sx", `${sx}px`);
      hero.style.setProperty("--sy", `${sy}px`);
      if (cursorRef.current) {
        cursorRef.current.style.setProperty("--cx", `${cx}px`);
        cursorRef.current.style.setProperty("--cy", `${cy}px`);
      }
      if (ctaRef.current) {
        ctaRef.current.style.setProperty("--mx", `${mx.toFixed(2)}px`);
        ctaRef.current.style.setProperty("--my", `${my.toFixed(2)}px`);
      }
    };

    const onEnterCta = () => {
      if (cursorRef.current) cursorRef.current.style.setProperty("--cscale", "1.6");
    };
    const onLeaveCta = () => {
      if (cursorRef.current) cursorRef.current.style.setProperty("--cscale", "1");
    };

    hero.addEventListener("pointermove", onMove);
    const cta = ctaRef.current;
    if (cta) {
      cta.addEventListener("pointerenter", onEnterCta);
      cta.addEventListener("pointerleave", onLeaveCta);
    }
    return () => {
      hero.removeEventListener("pointermove", onMove);
      if (cta) {
        cta.removeEventListener("pointerenter", onEnterCta);
        cta.removeEventListener("pointerleave", onLeaveCta);
      }
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className={styles.hero} ref={heroRef}>
      <div className={styles.spotlight} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <div className={`${styles.container} ${revealed ? styles.revealed : ""}`}>
        <div className={styles.brand} role="img" aria-label="Exhale">
          <span className={styles.brandText} aria-hidden="true">
            {typed}
          </span>
          <span className={styles.caret} aria-hidden="true" />
        </div>

        <p className={styles.tagline}>
          <span className={styles.taglineText}>{taglineTyped}</span>
          {taglineTyped && !taglineDone && (
            <span className={styles.taglineCaret} aria-hidden="true" />
          )}
        </p>

        <h1 className={styles.title}>
          <span className={styles.line1}>{t.line1}</span>
          {t.line2 && <span className={styles.line2}>{t.line2}</span>}
        </h1>

        <p className={styles.description}>{t.description}</p>

        <div className={styles.ctaGroup}>
          <a
            ref={ctaRef}
            href="https://calendar.app.google/4YtwnKduScqcFVjc9"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaButton}
          >
            {t.book}
          </a>
          <a href="#proof" className={styles.ctaSecondary}>
            {t.cases}
          </a>
        </div>
      </div>

      <div className={styles.cursor} ref={cursorRef} aria-hidden="true" />
    </section>
  );
};

export default HeroComponent;
